// S02/S05/S12 + B09/B10/B13 — sales & marketing calculators and simulators.
import { useMemo, useState } from "react";
import { funnelLeakage, incentiveSim, salesCapacity, budgetAllocation, campaignRoi, influencerRoi, inr, pct } from "./engines";
import { Field, SliderField, ResultRow, ToolSection, Note, useNumbers } from "./ui";

/* ---------- S02 Funnel Leakage ---------- */
const S02_DEFAULT = [
  { name: "Leads / enquiries", count: 500 },
  { name: "Qualified conversations", count: 200 },
  { name: "Proposals sent", count: 80 },
  { name: "Negotiation", count: 45 },
  { name: "Won", count: 24 },
];
export function ToolS02() {
  const [stages, setStages] = useState(S02_DEFAULT);
  const [adv, setAdv] = useState(100000);
  const r = useMemo(() => funnelLeakage({ stages, avgDealValue: adv }), [stages, adv]);
  const setS = (i, k) => (val) => setStages((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: val } : x)));
  return (
    <div data-testid="tool-S02" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        <Field label="Average deal value (₹)" value={adv} onChange={setAdv} testid="s02-adv" />
        {stages.map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-3 items-end border border-zinc-200 p-3" data-testid={`s02-stage-${i}`}>
            <div className="col-span-7">
              <input value={s.name} onChange={(e) => setS(i, "name")(e.target.value)} className="w-full text-sm font-medium outline-none border-b border-transparent focus:border-black" data-testid={`s02-name-${i}`} />
            </div>
            <div className="col-span-5">
              <Field label="Count / month" value={s.count} onChange={setS(i, "count")} testid={`s02-count-${i}`} />
            </div>
          </div>
        ))}
        <button type="button" onClick={() => setStages((s) => [...s, { name: `Stage ${s.length + 1}`, count: 0 }])} className="text-sm font-medium underline underline-offset-4" data-testid="s02-add">+ Add stage</button>
      </div>
      <div>
        <div data-testid="s02-result">
          <div className="space-y-2">
            {r.rows.slice(0, -1).map((row, i) => (
              <div key={i} className="grid grid-cols-12 items-center gap-3" data-testid={`s02-row-${i}`}>
                <span className="col-span-5 text-xs text-zinc-600 truncate">{row.name} → {stages[i + 1]?.name}</span>
                <div className="col-span-4 h-6 bg-zinc-100">
                  <div className={`h-full ${row.name === r.weakestStage ? "bg-red-700" : "bg-zinc-500"}`} style={{ width: `${Math.max(3, row.convPct || 0)}%` }} />
                </div>
                <span className="col-span-3 text-right text-xs tabular-nums text-zinc-700">{row.convPct}% · −{inr(row.lostValue)}</span>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <ResultRow label="End-to-end conversion" value={`${r.endToEndPct}%`} />
            <ResultRow label="Value lost in the funnel / month" value={inr(r.totalLostValue)} strong tone="bad" />
            <ResultRow label="Weakest stage" value={r.weakestStage || "—"} tone="bad" />
            {r.whatIf && <ResultRow label={`+10% relative conversion at "${r.whatIf.stage}"`} value={`+${r.whatIf.extraDeals} deals · +${inr(r.whatIf.extraValue)}`} tone="good" />}
          </div>
        </div>
        <Note tone="warn">Improve the weakest stage first. A 10% relative lift at the leakiest step compounds through every stage below it — top-of-funnel spend into a leaky funnel is subsidy.</Note>
      </div>
    </div>
  );
}

/* ---------- S05 Incentive Plan Simulator ---------- */
export function ToolS05() {
  const [v, set] = useNumbers({ fixedAnnual: 600000, variableTarget: 300000, thresholdPct: 70, acceleratorPct: 20, acceleratorFromPct: 110, capMultiple: 0 });
  const r = useMemo(() => incentiveSim(v), [v]);
  const maxVar = Math.max(...r.curve.map((c) => c.variable), 1);
  return (
    <div data-testid="tool-S05" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Fixed annual pay (₹)" value={v.fixedAnnual} onChange={set("fixedAnnual")} testid="s05-fixed" />
          <Field label="Variable at 100% (₹)" value={v.variableTarget} onChange={set("variableTarget")} testid="s05-variable" />
        </div>
        <SliderField label="Threshold — payout starts at %" value={v.thresholdPct} onChange={set("thresholdPct")} testid="s05-threshold" />
        <SliderField label="Accelerator above target" hint="% extra variable rate beyond the accelerator point." value={v.acceleratorPct} onChange={set("acceleratorPct")} testid="s05-acc" />
        <SliderField label="Accelerator starts at %" value={v.acceleratorFromPct} onChange={set("acceleratorFromPct")} min={100} max={150} testid="s05-accfrom" />
        <SliderField label="Cap (× target variable, 0 = uncapped)" value={v.capMultiple} onChange={set("capMultiple")} max={3} step={0.1} suffix="×" testid="s05-cap" />
      </div>
      <div>
        <div data-testid="s05-result">
          <div className="space-y-1.5">
            {r.curve.map((c) => (
              <div key={c.achievementPct} className="grid grid-cols-12 items-center gap-3" data-testid={`s05-row-${c.achievementPct}`}>
                <span className="col-span-3 text-xs text-zinc-600">{c.achievementPct}% of target</span>
                <div className="col-span-6 h-5 bg-zinc-100"><div className="h-full bg-zinc-700" style={{ width: `${(c.variable / maxVar) * 100}%` }} /></div>
                <span className="col-span-3 text-right text-xs tabular-nums text-zinc-700">{inr(c.variable)}</span>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <ResultRow label="Payout at 100%" value={inr(r.payoutAt100)} />
            <ResultRow label="Max payout in range" value={inr(r.maxPayout)} strong />
          </div>
        </div>
        {r.flags.length > 0 ? (
          <div className="mt-4 space-y-2" data-testid="s05-flags">
            {r.flags.map((f, i) => <Note key={i} tone="warn">{f}</Note>)}
          </div>
        ) : <Note>No structural red flags. Still test with last year's actual achievement distribution before launch.</Note>}
      </div>
    </div>
  );
}

/* ---------- S12 Sales Capacity Planner ---------- */
export function ToolS12() {
  const [v, set] = useNumbers({ revenueTarget: 50000000, avgDealSize: 500000, winRatePct: 25, meetingsPerWeekPerRep: 15, meetingsToClose: 6, workingWeeks: 46, currentReps: 3 });
  const r = useMemo(() => salesCapacity(v), [v]);
  return (
    <div data-testid="tool-S12" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Revenue target (₹/yr)" value={v.revenueTarget} onChange={set("revenueTarget")} testid="s12-target" />
          <Field label="Average deal size (₹)" value={v.avgDealSize} onChange={set("avgDealSize")} testid="s12-deal" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Win rate %" value={v.winRatePct} onChange={set("winRatePct")} testid="s12-win" />
          <Field label="Meetings to close" value={v.meetingsToClose} onChange={set("meetingsToClose")} testid="s12-mtc" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Meetings / rep / week" value={v.meetingsPerWeekPerRep} onChange={set("meetingsPerWeekPerRep")} testid="s12-mpw" />
          <Field label="Working weeks / yr" value={v.workingWeeks} onChange={set("workingWeeks")} testid="s12-weeks" />
        </div>
        <Field label="Current reps" value={v.currentReps} onChange={set("currentReps")} testid="s12-reps" />
      </div>
      <div>
        <div data-testid="s12-result">
          <ResultRow label="Deals needed" value={r.dealsNeeded.toLocaleString("en-IN")} />
          <ResultRow label="Qualified opportunities needed" value={r.oppsNeeded.toLocaleString("en-IN")} />
          <ResultRow label="Meetings required / year" value={r.meetingsNeeded.toLocaleString("en-IN")} />
          <ResultRow label="Opportunities one rep can run / year" value={String(r.oppsPerRepYear)} />
          <ResultRow label="Reps required" value={`${r.repsRequired} → hire ${r.repsRequiredCeil}`} strong />
          <ResultRow label="Verdict" value={r.verdict} tone={r.verdict === "under-resourced" ? "bad" : "good"} />
        </div>
        <Note tone={r.verdict === "under-resourced" ? "warn" : "info"}>
          {r.verdict === "under-resourced"
            ? `Short by ~${Math.abs(r.gap)} reps — and new hires take 3–6 months to ramp. The target needs either more people, a bigger deal size, or a better win rate. Say which, now.`
            : "Capacity matches the target on these assumptions. Stress-test the win rate — it's the assumption reps inflate most."}
        </Note>
      </div>
    </div>
  );
}

/* ---------- B09 Budget Allocation Simulator ---------- */
export function ToolB09() {
  const [v, set] = useNumbers({ budget: 5000000, brandPct: 40, performancePct: 45, retentionPct: 15, brandMultiplier: 1.5, perfRoas: 3, retentionRoas: 5 });
  const r = useMemo(() => budgetAllocation(v), [v]);
  const sum = Number(v.brandPct) + Number(v.performancePct) + Number(v.retentionPct);
  return (
    <div data-testid="tool-B09" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <Field label="Total marketing budget (₹/yr)" value={v.budget} onChange={set("budget")} testid="b09-budget" />
        <SliderField label="Brand building %" value={v.brandPct} onChange={set("brandPct")} testid="b09-brand" />
        <SliderField label="Performance / demand capture %" value={v.performancePct} onChange={set("performancePct")} testid="b09-perf" />
        <SliderField label="Retention & CRM %" value={v.retentionPct} onChange={set("retentionPct")} testid="b09-ret" />
        {sum !== 100 && <Note tone="warn">Allocations total {sum}% — normalised to 100% in the projection.</Note>}
        <div className="border-t border-black/10 pt-5 space-y-5">
          <div className="text-xs font-medium uppercase tracking-widest text-zinc-500">Your return assumptions (12-month)</div>
          <SliderField label="Brand multiplier (×)" value={v.brandMultiplier} onChange={set("brandMultiplier")} min={0} max={4} step={0.1} suffix="×" testid="b09-bm" />
          <SliderField label="Performance ROAS (×)" value={v.perfRoas} onChange={set("perfRoas")} min={0} max={10} step={0.5} suffix="×" testid="b09-pr" />
          <SliderField label="Retention ROAS (×)" value={v.retentionRoas} onChange={set("retentionRoas")} min={0} max={10} step={0.5} suffix="×" testid="b09-rr" />
        </div>
      </div>
      <div>
        <div data-testid="b09-result">
          <ResultRow label="Brand spend" value={inr(r.split.brand)} />
          <ResultRow label="Performance spend" value={inr(r.split.performance)} />
          <ResultRow label="Retention spend" value={inr(r.split.retention)} />
          <ResultRow label="Projected 12-month return" value={inr(r.totalReturn)} strong />
          <ResultRow label="Blended multiple" value={`${r.blendedMultiple}×`} tone={r.blendedMultiple >= 2 ? "good" : "bad"} />
        </div>
        {r.warnings.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="b09-warnings">
            {r.warnings.map((w, i) => <Note key={i} tone="warn">{w}</Note>)}
          </div>
        )}
        <Note>These are your assumptions made explicit, not predictions. The value of this tool is the conversation: which assumption would you bet the plan on, and what would prove it wrong?</Note>
      </div>
    </div>
  );
}

/* ---------- B10 Campaign ROI Forecaster ---------- */
export function ToolB10() {
  const [v, set] = useNumbers({ campaignCost: 500000, avgOrderValue: 2000, contributionPct: 30, baselineWeeklyOrders: 500, expectedUpliftPct: 20, durationWeeks: 4 });
  const r = useMemo(() => campaignRoi(v), [v]);
  return (
    <div data-testid="tool-B10" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <Field label="Total campaign cost (₹)" hint="Media + production + agency + influencer fees." value={v.campaignCost} onChange={set("campaignCost")} testid="b10-cost" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Avg order value (₹)" value={v.avgOrderValue} onChange={set("avgOrderValue")} testid="b10-aov" />
          <Field label="Contribution margin %" value={v.contributionPct} onChange={set("contributionPct")} testid="b10-cm" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Baseline orders /wk" value={v.baselineWeeklyOrders} onChange={set("baselineWeeklyOrders")} testid="b10-base" />
          <Field label="Expected uplift %" value={v.expectedUpliftPct} onChange={set("expectedUpliftPct")} testid="b10-uplift" />
          <Field label="Duration (wks)" value={v.durationWeeks} onChange={set("durationWeeks")} testid="b10-weeks" />
        </div>
      </div>
      <div>
        <div data-testid="b10-result">
          <ResultRow label="Contribution per order" value={inr(r.contributionPerOrder)} />
          <ResultRow label="Break-even incremental orders" value={r.breakEvenOrders === null ? "—" : r.breakEvenOrders.toLocaleString("en-IN")} strong />
          <ResultRow label="Break-even uplift needed" value={r.breakEvenUpliftPct === null ? "—" : `${r.breakEvenUpliftPct}%`} tone="bad" />
          <ResultRow label="Your scenario: incremental orders" value={r.incrementalOrders.toLocaleString("en-IN")} />
          <ResultRow label="Campaign ROI" value={r.roiPct === null ? "—" : `${r.roiPct}%`} strong tone={r.profitable ? "good" : "bad"} />
        </div>
        <Note tone={r.profitable ? "info" : "warn"}>
          {r.profitable
            ? "Profitable on these assumptions — now plan the measurement: holdout geography or pre/post with a matched control, agreed before launch."
            : `At ${Number(v.expectedUpliftPct) || 0}% uplift this loses money. Either the campaign needs a bigger effect, a longer measurement window (brand effects outlast the flight), or a smaller budget.`}
        </Note>
      </div>
    </div>
  );
}

/* ---------- B13 Influencer ROI + Brief ---------- */
export function ToolB13() {
  const [v, set] = useNumbers({ fee: 300000, deliverableCost: 50000, reach: 500000, engagementPct: 3, clickPct: 20, conversionPct: 2, avgOrderValue: 1500, contributionPct: 40 });
  const r = useMemo(() => influencerRoi(v), [v]);
  const [brief, setBrief] = useState({ objective: "Drive first-purchase trials of the new variant", audience: "Women 22–34, metro + tier-1, skincare routine buyers", mandatories: "Show the texture close-up; state price honestly; #ad disclosure", deadline: "" });
  return (
    <div data-testid="tool-B13">
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Creator fee (₹)" value={v.fee} onChange={set("fee")} testid="b13-fee" />
            <Field label="Boosting / deliverable cost (₹)" value={v.deliverableCost} onChange={set("deliverableCost")} testid="b13-deliv" />
          </div>
          <Field label="Expected reach" value={v.reach} onChange={set("reach")} testid="b13-reach" />
          <div className="grid grid-cols-3 gap-4">
            <Field label="Engagement %" value={v.engagementPct} onChange={set("engagementPct")} testid="b13-eng" />
            <Field label="Click-through % of engaged" value={v.clickPct} onChange={set("clickPct")} testid="b13-click" />
            <Field label="Conversion % of visits" value={v.conversionPct} onChange={set("conversionPct")} testid="b13-conv" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="AOV (₹)" value={v.avgOrderValue} onChange={set("avgOrderValue")} testid="b13-aov" />
            <Field label="Contribution %" value={v.contributionPct} onChange={set("contributionPct")} testid="b13-cm" />
          </div>
        </div>
        <div>
          <div data-testid="b13-result">
            <ResultRow label="Total cost" value={inr(r.totalCost)} />
            <ResultRow label="Projected orders" value={r.orders.toLocaleString("en-IN")} />
            <ResultRow label="Break-even orders" value={r.breakEvenOrders === null ? "—" : r.breakEvenOrders.toLocaleString("en-IN")} strong />
            <ResultRow label="Cost per order" value={r.costPerOrder === null ? "—" : inr(r.costPerOrder)} />
            <ResultRow label="Deal ROI" value={r.roiPct === null ? "—" : `${r.roiPct}%`} strong tone={r.profitable ? "good" : "bad"} />
          </div>
          <Note tone={r.profitable ? "info" : "warn"}>
            {r.profitable ? "Viable on direct economics — before brand value." : `Direct economics need ${r.breakEvenOrders} orders; the funnel projects ${r.orders}. Renegotiate fee, add an affiliate code, or treat the gap as a brand spend with eyes open.`}
          </Note>
        </div>
      </div>
      <ToolSection title="The brief that makes it work" testid="b13-brief">
        <div className="grid md:grid-cols-2 gap-4">
          {[["objective", "Single objective"], ["audience", "Who exactly"], ["mandatories", "Mandatories & disclosures"], ["deadline", "Deadline & review gate"]].map(([k, label]) => (
            <label key={k} className="block">
              <span className="text-sm font-medium text-zinc-800">{label}</span>
              <textarea value={brief[k]} onChange={(e) => setBrief((s) => ({ ...s, [k]: e.target.value }))} rows={2} className="mt-1.5 w-full border border-zinc-300 p-2.5 text-sm outline-none focus:border-black" data-testid={`b13-brief-${k}`} />
            </label>
          ))}
        </div>
        <Note>Compliance is not optional: ASCI guidelines require material-connection disclosure (#ad / paid partnership). Put it in mandatories, not in a post-contract argument.</Note>
      </ToolSection>
    </div>
  );
}
